import Axios from "axios";
import {appConfig} from "./app-config";
import {getUser} from "./storage";

const enabled = String(process.env.REACT_APP_ERROR_MONITORING_ENABLED ?? "true").toLowerCase() !== "false";
const release = process.env.REACT_APP_ERROR_MONITORING_RELEASE || process.env.REACT_APP_VERSION || "development";
const MAX_BREADCRUMBS = 40;
const capturedErrors = typeof WeakSet === "function" ? new WeakSet() : null;
const SENSITIVE_TRACE_KEY = /(password|passcode|otp|token|authorization|cookie|secret|api[-_]?key|session|credential|private[-_]?key)/i;

let started = false;
let context = {
    workspaceDocumentId: null,
    membershipId: null,
    sessionKey: null,
    route: null,
};
let breadcrumbs = [];

function eventId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function requestId() {
    return eventId();
}

function stripUrl(value) {
    const text = String(value || "");
    try {
        const parsed = new URL(text, window.location.origin);
        return parsed.pathname;
    } catch (_) {
        return text.split(/[?#]/, 1)[0];
    }
}

function normalizeError(error) {
    if (error instanceof Error) return error;
    if (typeof error === "string") return new Error(error);
    try {
        return new Error(JSON.stringify(error));
    } catch (_) {
        return new Error("UnknownError");
    }
}

function connectionState() {
    if (!navigator.onLine) return "offline";
    return navigator.connection?.effectiveType || "online";
}

function redactTraceText(value) {
    return String(value ?? "")
        .slice(0, 4000)
        .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [REDACTED]")
        .replace(/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}(?:\.[A-Za-z0-9_-]{10,})?\b/g, "[REDACTED_TOKEN]");
}

function safeTraceValue(value, key = "", depth = 0) {
    if (SENSITIVE_TRACE_KEY.test(key)) return "[REDACTED]";
    if (value === null || value === undefined || typeof value === "boolean" || typeof value === "number") return value;
    if (typeof value === "string") {
        const trimmed = value.trim();
        if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
            try { return safeTraceValue(JSON.parse(trimmed), key, depth); } catch (_) { /* keep the original text */ }
        }
        return redactTraceText(value);
    }
    if (depth >= 5) return "[TRUNCATED]";
    if (Array.isArray(value)) return value.slice(0, 80).map((item) => safeTraceValue(item, key, depth + 1));
    if (typeof value !== "object") return redactTraceText(value);
    const normalized = typeof value.toJSON === "function" ? value.toJSON() : value;
    return Object.fromEntries(Object.entries(normalized).slice(0, 80)
        .map(([nestedKey, nestedValue]) => [nestedKey, safeTraceValue(nestedValue, nestedKey, depth + 1)]));
}

function httpTrace(error) {
    const config = error?.config || {};
    const response = error?.response;
    return safeTraceValue({
        request: {
            method: String(config.method || "GET").toUpperCase(),
            url: stripUrl(config.url),
            query: config.params ?? null,
            headers: config.headers ?? null,
            payload: config.data ?? null,
            timeout: config.timeout ?? null,
        },
        response: response ? {
            status: response.status ?? null,
            statusText: response.statusText || null,
            headers: response.headers ?? null,
            payload: response.data ?? null,
        } : null,
    });
}

function safeMetadata(value) {
    if (!value || typeof value !== "object") return {};
    const allowed = [
        "code", "status", "method", "timeout", "retryable", "componentStack",
        "apiRequestId", "errorKind",
    ];
    const metadata = Object.fromEntries(allowed
        .filter((key) => value[key] !== undefined && value[key] !== null)
        .map((key) => [key, String(value[key]).slice(0, 2000)]));
    if (value.http) metadata.http = safeTraceValue(value.http);
    return metadata;
}

function markCaptured(error) {
    if (!capturedErrors || !(error instanceof Error)) return false;
    if (capturedErrors.has(error)) return true;
    capturedErrors.add(error);
    return false;
}

export function updateErrorMonitoringContext(nextContext = {}) {
    context = {
        ...context,
        workspaceDocumentId: nextContext.workspaceDocumentId || null,
        membershipId: nextContext.membershipId || null,
        sessionKey: nextContext.sessionKey || null,
        route: stripUrl(nextContext.route || window.location.pathname),
    };
}

export function addErrorBreadcrumb(category, data = {}) {
    if (!enabled) return;
    const item = {
        category: String(category || "application").slice(0, 80),
        at: new Date().toISOString(),
        data: safeMetadata(data),
    };
    breadcrumbs = [...breadcrumbs, item].slice(-MAX_BREADCRUMBS);
}

async function send(payload) {
    if (!enabled || !getUser()?.jwt || !context.workspaceDocumentId) return null;
    const user = getUser();
    try {
        const response = await Axios.post(`${appConfig.apiUrl}/error-monitoring/events`, payload, {
            timeout: 5000,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.jwt}`,
                workspace: context.workspaceDocumentId,
                "X-Request-ID": payload.requestId || requestId(),
            },
        });
        return response.data;
    } catch (_) {
        return null;
    }
}

export function captureException(rawError, options = {}) {
    if (!enabled) return Promise.resolve(null);
    const error = normalizeError(rawError);
    if (!options.allowDuplicate && markCaptured(error)) return Promise.resolve(null);
    const payload = {
        eventId: options.eventId || eventId(),
        occurredAt: new Date().toISOString(),
        source: "frontend",
        exceptionType: error.name || "Error",
        message: error.message || "Unexpected error",
        stack: error.stack || "",
        handled: options.handled === true,
        route: context.route || window.location.pathname,
        url: window.location.pathname,
        endpoint: stripUrl(options.endpoint),
        httpMethod: options.httpMethod,
        httpStatus: options.httpStatus,
        requestId: options.requestId,
        sessionKey: context.sessionKey,
        environment: process.env.NODE_ENV || "production",
        release,
        browser: navigator.userAgent,
        networkState: connectionState(),
        breadcrumbs,
        metadata: safeMetadata(options.metadata),
    };
    return send(payload);
}

export function captureApiError(error) {
    const status = Number(error?.response?.status || 0);
    const timeout = error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT";
    const network = !error?.response && navigator.onLine;
    if (status < 500 && !timeout && !network) return Promise.resolve(null);
    const responseRequestId = error?.response?.headers?.["x-request-id"];
    return captureException(error, {
        handled: true,
        endpoint: error?.config?.url,
        httpMethod: error?.config?.method,
        httpStatus: status || null,
        requestId: responseRequestId || error?.config?.headers?.["X-Request-ID"],
        metadata: {
            code: error?.code,
            status,
            timeout,
            apiRequestId: responseRequestId,
            errorKind: timeout ? "timeout" : network ? "network" : "server",
            http: httpTrace(error),
        },
    });
}

export function initializeErrorMonitoring() {
    if (!enabled || started) return () => undefined;
    started = true;
    const onError = (event) => {
        captureException(event.error || new Error(event.message || "WindowError"), {
            handled: false,
            metadata: {errorKind: "window"},
        });
    };
    const onUnhandledRejection = (event) => {
        captureException(event.reason || new Error("UnhandledRejection"), {
            handled: false,
            metadata: {errorKind: "unhandledrejection"},
        });
    };
    const onRoute = () => addErrorBreadcrumb("navigation", {});
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("popstate", onRoute);
    return () => {
        window.removeEventListener("error", onError);
        window.removeEventListener("unhandledrejection", onUnhandledRejection);
        window.removeEventListener("popstate", onRoute);
        started = false;
    };
}

export function createApiRequestId() {
    return requestId();
}
